import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Gap } from "@/types/audit";
import type { GapFillCandidate } from "@/types/gapFill";

interface Props {
  isOpen: boolean;
  gap: Gap | null;
  candidates: GapFillCandidate[];
  isAccepting?: boolean;
  onAccept: (paperIds: string[]) => void;
  onClose: () => void;
}

function scoreColor(score: number): string {
  if (score >= 80) return "#a6e3a1";
  if (score >= 60) return "#89b4fa";
  if (score >= 40) return "#f9e2af";
  return "#f38ba8";
}

// Caption format from `caption_gap_fit`: "Fills gap: <reason>." or
// "Doesn't fill gap: <reason>.". Split so the modal can render the
// verdict as a colored chip ahead of the reasoning text. Falls back
// to {verdict: null, reason: <full caption>} for legacy / off-format
// captions so nothing disappears if Haiku drifts from the prompt.
function parseCaption(
  caption: string,
): { verdict: "fills" | "doesnt" | null; reason: string } {
  const trimmed = caption.trim();
  const fillsMatch = /^fills gap\s*:\s*/i.exec(trimmed);
  if (fillsMatch) {
    return { verdict: "fills", reason: trimmed.slice(fillsMatch[0].length) };
  }
  const doesntMatch = /^doesn['’]t fill gap\s*:\s*/i.exec(trimmed);
  if (doesntMatch) {
    return { verdict: "doesnt", reason: trimmed.slice(doesntMatch[0].length) };
  }
  return { verdict: null, reason: trimmed };
}

export function GapFillModal({
  isOpen,
  gap,
  candidates,
  isAccepting = false,
  onAccept,
  onClose,
}: Props) {
  // Sort by score desc so the highest-scoring matches show first.
  // Backend also sorts in the runner, but we re-sort client-side as
  // belt-and-braces in case the payload arrives unordered (resume
  // path, future cache replays, etc.).
  const sortedCandidates = useMemo(
    () => [...candidates].sort((a, b) => b.score - a.score),
    [candidates],
  );

  const defaultSelected = useMemo(
    () =>
      new Set(
        sortedCandidates.filter((c) => c.score >= 60).map((c) => c.paper_id),
      ),
    [sortedCandidates],
  );
  const [selected, setSelected] = useState<Set<string>>(defaultSelected);

  useEffect(() => {
    setSelected(defaultSelected);
  }, [defaultSelected]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(sortedCandidates.map((c) => c.paper_id)));
  };

  const selectNone = () => {
    setSelected(new Set());
  };

  if (!gap) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden p-0 sm:max-w-3xl md:max-w-4xl">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="flex items-center gap-2 text-base">
            <span>Fill gap:</span>
            <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
              {gap.kind}
            </span>
            <span className="text-muted-foreground">·</span>
            <span className="font-medium">{gap.tag}</span>
          </DialogTitle>
          <DialogDescription className="mt-1 text-sm">
            {gap.description}
          </DialogDescription>
        </DialogHeader>

        <div className="border-b px-5 py-2 text-xs text-muted-foreground">
          {candidates.length === 0
            ? "0 candidates"
            : `${candidates.length} candidate${
                candidates.length === 1 ? "" : "s"
              }`}
          {candidates.length > 0 && (
            <span className="ml-3 inline-flex gap-2">
              <button
                type="button"
                className="text-xs underline-offset-2 hover:underline"
                onClick={selectAll}
              >
                Select all
              </button>
              <button
                type="button"
                className="text-xs underline-offset-2 hover:underline"
                onClick={selectNone}
              >
                None
              </button>
            </span>
          )}
        </div>

        <div className="max-h-[55vh] overflow-y-auto px-5 py-3">
          {sortedCandidates.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">
              No papers matched these queries. Edit the gap description and
              re-run the audit, or refine your concept&rsquo;s keywords.
            </p>
          ) : (
            <ul className="space-y-3">
              {sortedCandidates.map((c) => {
                const dimmed = c.score < 40;
                const color = scoreColor(c.score);
                return (
                  <li
                    key={c.paper_id}
                    className={`flex items-start gap-3 rounded border border-border bg-card/40 p-3 ${
                      dimmed ? "opacity-60" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      aria-label={c.paper_id}
                      checked={selected.has(c.paper_id)}
                      onChange={() => toggle(c.paper_id)}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className="rounded px-1.5 py-0.5 font-mono text-xs"
                          style={{
                            backgroundColor: `${color}20`,
                            color,
                          }}
                        >
                          [{Math.round(c.score)}]
                        </span>
                        <span className="font-medium leading-snug">
                          {c.title}
                        </span>
                      </div>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {c.authors.slice(0, 3).join(", ")}
                        {c.authors.length > 3 ? " et al." : ""}
                        {" · "}
                        {c.year ?? "n.d."}
                        {" · "}
                        {c.citation_count} cite
                        {c.citation_count === 1 ? "" : "s"}
                      </div>
                      {c.gap_fit_caption ? (
                        (() => {
                          const { verdict, reason } = parseCaption(
                            c.gap_fit_caption,
                          );
                          const verdictColor =
                            verdict === "fills"
                              ? "#a6e3a1"
                              : verdict === "doesnt"
                                ? "#f38ba8"
                                : null;
                          return (
                            <div
                              className="mt-1.5 border-l-2 pl-2"
                              style={{ borderColor: `${color}80` }}
                            >
                              {verdict && verdictColor && (
                                <span
                                  className="mr-1.5 inline-block rounded px-1.5 py-0.5 align-middle font-mono text-[10px] uppercase tracking-wide"
                                  style={{
                                    backgroundColor: `${verdictColor}20`,
                                    color: verdictColor,
                                  }}
                                >
                                  {verdict === "fills"
                                    ? "Fills gap"
                                    : "Doesn’t fill"}
                                </span>
                              )}
                              <span
                                className="text-sm italic"
                                style={{
                                  color: dimmed
                                    ? undefined
                                    : "rgb(165, 180, 252)",
                                }}
                              >
                                {reason}
                              </span>
                            </div>
                          );
                        })()
                      ) : (
                        <p className="mt-1.5 text-sm italic text-muted-foreground">
                          (gap-fit caption unavailable — candidate ranked by
                          concept score only)
                        </p>
                      )}
                      <p className="mt-1 truncate text-xs text-muted-foreground">
                        via query: {c.source_query}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 border-t px-5 py-3">
          <Button variant="ghost" onClick={onClose} disabled={isAccepting}>
            Cancel
          </Button>
          <Button
            onClick={() => onAccept(Array.from(selected))}
            disabled={selected.size === 0 || isAccepting}
          >
            {isAccepting
              ? "Adding…"
              : `Add ${selected.size} paper${selected.size === 1 ? "" : "s"}`}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}
